import React, { useState, useEffect } from 'react';
import { analyticsApi, blogApi } from '../services/api';
import { AnalyticsSummary, BlogPost } from '../types';

const cardStyle: React.CSSProperties = {
  background: 'white',
  padding: '1.5rem',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
};

const Analytics: React.FC = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadData(days);
  }, [days]);

  const loadData = async (rangeDays: number) => {
    setLoading(true);
    try {
      const [summaryData, postsData] = await Promise.all([
        analyticsApi.getSummary(rangeDays),
        blogApi.getAllPosts(),
      ]);
      setSummary(summaryData);
      setPosts(postsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const postTitle = (postId: string): string => {
    const post = posts.find(p => String(p.id) === postId);
    return post ? post.title : `Post #${postId}`;
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading analytics...</div>;
  if (!summary) return <div style={{ padding: '2rem' }}>Failed to load analytics.</div>;

  const sortedPaths = Object.entries(summary.pageViewsByPath).sort((a, b) => b[1] - a[1]);
  const sortedPosts = Object.entries(summary.postReadsByPostId).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ color: '#333' }}>Analytics</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{ padding: '0.5rem 1rem', borderRadius: '4px', border: '1px solid #ccc' }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h3 style={{ marginBottom: '1rem', color: '#666', fontSize: '1rem' }}>Unique Visitors</h3>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#007bff' }}>
            {summary.uniqueVisitors}
          </div>
        </div>
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <h3 style={{ marginBottom: '1rem', color: '#666', fontSize: '1rem' }}>Total Page Views</h3>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#007bff' }}>
            {summary.totalPageViews}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
        <div style={cardStyle}>
          <h2 style={{ color: '#333', marginBottom: '1rem', fontSize: '1.25rem' }}>Page Views</h2>
          {sortedPaths.length === 0 && <div style={{ color: '#666' }}>No data yet.</div>}
          {sortedPaths.map(([path, views]) => (
            <div key={path} style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '0.5rem 0', borderBottom: '1px solid #eee'
            }}>
              <span>{path}</span>
              <strong>{views}</strong>
            </div>
          ))}
        </div>

        <div style={cardStyle}>
          <h2 style={{ color: '#333', marginBottom: '1rem', fontSize: '1.25rem' }}>Blog Post Reads</h2>
          {sortedPosts.length === 0 && <div style={{ color: '#666' }}>No data yet.</div>}
          {sortedPosts.map(([postId, views]) => (
            <div key={postId} style={{
              display: 'flex', justifyContent: 'space-between', gap: '1rem',
              padding: '0.5rem 0', borderBottom: '1px solid #eee'
            }}>
              <span>{postTitle(postId)}</span>
              <strong>{views}</strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
